import { useState, useEffect, useCallback } from 'react';
import {
	PANEL_MIN_WIDTH,
	PANEL_MAX_WIDTH,
	PANEL_MIN_HEIGHT,
	PANEL_MAX_HEIGHT,
	PANEL_DEFAULT_WIDTH,
	PANEL_TOP_PADDING,
	PANEL_VIEWPORT_MARGIN,
	BUTTON_SIZE,
	BUTTON_PANEL_GAP,
	BUTTON_DEFAULT_POSITION,
} from './constants';

export function calculatePanelHeight(buttonBottomPx: number): number {
	const viewportHeight = window.innerHeight;
	const panelBottom = buttonBottomPx + BUTTON_SIZE + BUTTON_PANEL_GAP;
	const availableSpace = viewportHeight - panelBottom - PANEL_TOP_PADDING;
	const capped = Math.min(availableSpace, viewportHeight - PANEL_VIEWPORT_MARGIN, PANEL_MAX_HEIGHT);
	return Math.max(PANEL_MIN_HEIGHT, capped);
}

export const useResizeLogic = () => {
	const [buttonPosition, setButtonPosition] = useState(BUTTON_DEFAULT_POSITION);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStartY, setDragStartY] = useState(0);
	const [dragStartPosition, setDragStartPosition] = useState(0);
	const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
	const [panelHeight, setPanelHeight] = useState(() => calculatePanelHeight(BUTTON_DEFAULT_POSITION));
	const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
	const [userResizedHeight, setUserResizedHeight] = useState(false);

	const handleMouseDown = (e: React.MouseEvent) => {
		setIsDragging(true);
		setDragStartY(e.clientY);
		setDragStartPosition(buttonPosition);
		e.preventDefault();
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (!isDragging) return;
		const deltaY = dragStartY - e.clientY;
		const newPosition = Math.max(24, Math.min(window.innerHeight - 100, dragStartPosition + deltaY));
		setButtonPosition(newPosition);
		setUserResizedHeight(false);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	useEffect(() => {
		if (isDragging) {
			window.addEventListener('mousemove', handleMouseMove);
			window.addEventListener('mouseup', handleMouseUp);
			return () => {
				window.removeEventListener('mousemove', handleMouseMove);
				window.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [isDragging, dragStartY, dragStartPosition, buttonPosition]);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout>;
		const handleResize = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				setViewportHeight(window.innerHeight);
				setPanelHeight(calculatePanelHeight(buttonPosition));
			}, 100);
		};
		window.addEventListener('resize', handleResize);
		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', handleResize);
		};
	}, [buttonPosition]);

	useEffect(() => {
		setPanelHeight(calculatePanelHeight(buttonPosition));
	}, [buttonPosition]);

	const panelPositionStyle = (() => {
		const margin = 10;
		const gap = BUTTON_PANEL_GAP;
		const buttonTop = viewportHeight - buttonPosition - BUTTON_SIZE;
		const spaceAbove = buttonTop - margin;
		const spaceBelow = buttonPosition - margin;

		if (spaceAbove >= PANEL_MIN_HEIGHT) {
			const autoH = Math.min(PANEL_MAX_HEIGHT, spaceAbove);
			const h = userResizedHeight ? Math.min(panelHeight, spaceAbove) : autoH;
			return {
				height: `${h}px`,
				bottom: `${buttonPosition + BUTTON_SIZE + gap}px`,
				top: 'auto' as const,
			};
		} else {
			const autoH = Math.min(PANEL_MAX_HEIGHT, spaceBelow);
			const h = userResizedHeight ? Math.min(panelHeight, spaceBelow) : autoH;
			return {
				height: `${h}px`,
				bottom: 'auto' as const,
				top: `${buttonTop + BUTTON_SIZE + gap}px`,
			};
		}
	})();

	const handlePanelResize = useCallback(({ dx, dy }: { dx: number; dy: number }) => {
		setPanelWidth((prev) => Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, prev - dx)));
		setPanelHeight((prev) => Math.max(PANEL_MIN_HEIGHT, Math.min(window.innerHeight - PANEL_VIEWPORT_MARGIN, prev - dy)));
		if (dy !== 0) setUserResizedHeight(true);
	}, []);

	const handlePanelResizeEnd = useCallback(() => {}, []);

	return {
		buttonPosition,
		isDragging,
		panelWidth,
		panelHeight,
		panelPositionStyle,
		handleMouseDown,
		handlePanelResize,
		handlePanelResizeEnd,
	};
};
